/**
 * /dashboard/posh/training — POSH training module list.
 *
 * Migration note (Phase 3f — 2026-06-01): the per-module scenario player
 * + quiz routes (/dashboard/posh/training/$moduleId, /quiz) are now
 * ported. Card CTAs swapped from <a href> to typed Link.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { useMemo } from "react";
import { Award, Clock, GraduationCap } from "lucide-react";
import { localize } from "@/lib/api";
import { useCertificates, useTrainingModules } from "@/hooks/use-posh";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { PoshIcon } from "@/components/posh/posh-icons";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { toApiError } from "@/lib/api";
import type { ModuleWithProgress } from "@/types/posh";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/posh/training/")({
  component: TrainingListRoute,
});

function pct(m: ModuleWithProgress): number {
  const s = m.progress?.status;
  if (s === "completed") return 100;
  if (s === "in_progress") return 50;
  return 0;
}

function ListSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-10 w-10 rounded-lg" />
            <Skeleton className="mt-3 h-5 w-2/3" />
            <Skeleton className="mt-2 h-4 w-full" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-2 w-full" />
          </CardContent>
          <CardFooter>
            <Skeleton className="h-9 w-28" />
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function TrainingList() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const modulesQ = useTrainingModules();
  const certsQ = useCertificates();

  const certByModule = useMemo(() => {
    const map = new Map<number, boolean>();
    for (const c of certsQ.data?.certificates ?? []) {
      if (!c.is_revoked) map.set(c.module_id, true);
    }
    return map;
  }, [certsQ.data]);

  if (modulesQ.isLoading) return <ListSkeleton />;

  if (modulesQ.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load training modules</AlertTitle>
        <AlertDescription>
          {toApiError(modulesQ.error).message} Please refresh to try
          again.
        </AlertDescription>
      </Alert>
    );
  }

  const modules = [...(modulesQ.data?.modules ?? [])].sort(
    (a, b) => a.module.sort_order - b.module.sort_order,
  );

  if (modules.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground">
            <GraduationCap className="h-6 w-6" />
          </span>
          <p className="max-w-sm text-sm text-muted-foreground">
            No training modules are available yet. They will appear here
            once your administrator publishes the POSH curriculum.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {modules.map(({ module, progress }) => {
        const hasCert = certByModule.get(module.id) ?? false;
        const status = progress?.status ?? "not_started";
        const p = pct({ module, progress });
        return (
          <Card key={module.id} className="flex flex-col">
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                  <PoshIcon name={module.icon} className="h-5 w-5" />
                </span>
                <div className="flex flex-col items-end gap-1.5">
                  <StatusBadge status={status} />
                  {hasCert && (
                    <Badge className="gap-1 bg-green-100 text-green-800 hover:bg-green-100">
                      <Award className="h-3 w-3" />
                      Certified
                    </Badge>
                  )}
                </div>
              </div>
              <CardTitle className="mt-3 text-base">
                {localize(module.title) || module.code}
              </CardTitle>
              <CardDescription>
                {localize(module.description)}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 space-y-3">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Progress</span>
                  <span>{p}%</span>
                </div>
                <Progress value={p} className="h-2" />
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                <span>{module.duration_minutes} min</span>
                {module.is_mandatory && (
                  <span className="ml-1 rounded bg-accent px-1.5 py-0.5 text-[11px] font-medium text-accent-foreground">
                    Mandatory
                  </span>
                )}
              </div>
            </CardContent>
            <CardFooter className="gap-2">
              <Button asChild className="flex-1">
                <Link
                  to="/app/$tenantSlug/posh/training/$moduleId"
                  params={{tenantSlug, moduleId: String(module.id)}}
                >
                  {status === "completed"
                    ? "Review"
                    : status === "in_progress"
                      ? "Continue"
                      : "Start"}
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link
                  to="/app/$tenantSlug/posh/training/$moduleId/quiz"
                  params={{tenantSlug, moduleId: String(module.id)}}
                >
                  Quiz
                </Link>
              </Button>
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
}

function TrainingListRoute() {
  return (
    <PoshModuleShell
      title="POSH Training"
      description="Work through each scenario, then pass the module quiz to earn your certificate."
    >
      <PoshFeatureGate>
        <TrainingList />
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
